import { revalidateLogic } from "@tanstack/react-form";
import { useMutation } from "@tanstack/react-query";
import { createFileRoute, Link, redirect, useRouter } from "@tanstack/react-router";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { z } from "zod";
import logo from "@/assets/landing/logo-rocortado.png";
import { PhoneInput } from "@/components/form/phone-input";
import { useAppForm } from "@/components/form/tanstack-form";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/ui/input-password";
import { parsePocketBaseError } from "@/lib/errors/pocketbase";
import { pb } from "@/lib/pocketbase";
import { passwordSchema, phoneNumberSchema, type SafeOmit } from "@/lib/utils";
import { Collections, type Create } from "@/types/pocketbase.ts";

const validateSearch = z.object({
	invitation_token: z.string().optional(),
});

export const Route = createFileRoute("/auth/register")({
	validateSearch,
	loaderDeps: ({ search }) => ({ invitation_token: search.invitation_token }),
	beforeLoad: ({ search }) => {
		if (pb.authStore.isValid) {
			throw redirect({ to: "/dashboard", search });
		}
	},

	component: RouteComponent,
});

const formSchema = z
	.object({
		email: z.email(),
		phoneNumber: phoneNumberSchema(),
		password: passwordSchema,
		passwordConfirm: z.string(),
	})
	.refine((data) => data.password === data.passwordConfirm, {
		path: ["passwordConfirm"],
		// TODO: Add to error map
		message: "Las contraseñas no coinciden",
	});

type FormValues = z.infer<typeof formSchema>;

function RouteComponent() {
	const router = useRouter();
	const { t } = useTranslation();
	const search = Route.useSearch();

	const register = useMutation({
		mutationFn: async (payload: FormValues) => {
			toast.loading(
				t(($) => $.modules.auth.register.status.loading.title),
				{
					id: "register",
					description: t(($) => $.modules.auth.register.status.loading.message),
				}
			);

			const values: SafeOmit<Create<"doctors">, "tokenKey"> = {
				...payload,
				email: payload.email.toLowerCase(),
			};

			await pb.collection(Collections.Doctors).create(values);

			return values;
		},
		onSuccess: async ({ email, password }) => {
			toast.success(
				t(($) => $.modules.auth.register.status.success.title),
				{
					id: "register",
					description: t(($) => $.modules.auth.register.status.success.message),
				}
			);

			await pb.collection(Collections.Doctors).authWithPassword(email, password);

			await router.navigate({ to: "/dashboard", search });
		},
		onError: (error) => {
			const parsed = parsePocketBaseError(error, defaultValues);

			if (parsed.unknown) {
				return toast.error(
					t(($) => $.modules.auth.register.status.error.title),
					{
						id: "register",
						description: parsed.message || t(($) => $.modules.auth.register.status.error.message),
					}
				);
			}

			form.setErrorMap({ onDynamic: parsed.errorMap });
			toast.dismiss("register");
		},
	});

	const defaultValues: FormValues = {
		email: "",
		phoneNumber: "",
		password: "",
		passwordConfirm: "",
	};

	const form = useAppForm({
		defaultValues,
		validationLogic: revalidateLogic({
			mode: "submit",
			modeAfterSubmission: "change",
		}),
		validators: { onDynamic: formSchema },
		onSubmit: ({ value }) => {
			register.mutate(value);
		},
	});

	const handleSubmit = useCallback(
		(e: React.SubmitEvent) => {
			e.preventDefault();
			e.stopPropagation();
			form.handleSubmit();
		},
		[form]
	);

	return (
		<div className="mx-auto w-full max-w-md space-y-8">
			<div className="flex flex-col items-center">
				<img alt="Logo" height={"192.667"} src={logo} width={"447.967"} />

				<h2 className="mt-6 text-center font-bold text-3xl text-foreground tracking-tight">
					{t(($) => $.modules.auth.register.title)}
				</h2>
			</div>

			<form.AppForm>
				<form className="space-y-6" onSubmit={handleSubmit}>
					<form.AppField name="email">
						{(field) => (
							<field.Field>
								<field.FieldLabel>{t(($) => $.modules.auth.common.fields.email.label)}</field.FieldLabel>
								<field.FieldControl>
									<Input
										autoComplete="email"
										onBlur={field.handleBlur}
										onChange={(e) => field.handleChange(e.target.value)}
										placeholder={t(($) => $.modules.auth.common.fields.email.placeholder)}
										type="email"
										value={field.state.value}
									/>
								</field.FieldControl>
								<field.FieldError />
							</field.Field>
						)}
					</form.AppField>

					<form.AppField name="phoneNumber">
						{(field) => (
							<field.Field>
								<field.FieldLabel>{t(($) => $.modules.auth.common.fields.phoneNumber.label)}</field.FieldLabel>
								<field.FieldControl>
									<PhoneInput
										autoComplete="tel"
										onBlur={field.handleBlur}
										onChange={(value) => field.handleChange(value)}
										placeholder={t(($) => $.modules.auth.common.fields.phoneNumber.placeholder)}
										value={field.state.value}
									/>
								</field.FieldControl>
								<field.FieldError />
							</field.Field>
						)}
					</form.AppField>

					<form.AppField name="password">
						{(field) => (
							<field.Field>
								<field.FieldLabel>{t(($) => $.modules.auth.common.fields.password.label)}</field.FieldLabel>
								<field.FieldControl>
									<PasswordInput
										autoComplete="new-password"
										onBlur={field.handleBlur}
										onChange={(e) => field.handleChange(e.target.value)}
										placeholder={t(($) => $.modules.auth.common.fields.password.placeholder)}
										value={field.state.value}
									/>
								</field.FieldControl>
								<field.FieldError />
							</field.Field>
						)}
					</form.AppField>

					<form.AppField name="passwordConfirm">
						{(field) => (
							<field.Field>
								<field.FieldLabel>{t(($) => $.modules.auth.register.fields.passwordConfirm.label)}</field.FieldLabel>
								<field.FieldControl>
									<PasswordInput
										autoComplete="new-password"
										onBlur={field.handleBlur}
										onChange={(e) => field.handleChange(e.target.value)}
										placeholder={t(($) => $.modules.auth.register.fields.passwordConfirm.placeholder)}
										value={field.state.value}
									/>
								</field.FieldControl>
								<field.FieldError />
							</field.Field>
						)}
					</form.AppField>

					<Button className="w-full" disabled={register.isPending} type="submit">
						{t(($) => $.modules.auth.register.buttons.submit)}
					</Button>
				</form>
			</form.AppForm>

			<div className="mt-4 text-center">
				<span className="text-muted-foreground text-sm">
					{t(($) => $.modules.auth.register.buttons.login.question)}
				</span>{" "}
				<Link className="font-medium text-primary text-sm hover:underline" to="/auth/login">
					{t(($) => $.modules.auth.register.buttons.login.answer)}
				</Link>
			</div>
		</div>
	);
}
